<template>
  <Teleport to="body">
    <div class="agp-backdrop" @click="emit('close')"></div>
    <aside ref="root" class="agp" role="dialog" aria-label="Agendor" @keydown.esc="emit('close')">
      <header class="agp-head">
        <div>
          <span class="agp-brand">Agendor</span>
          <h2 class="agp-title">{{ link?.personName ?? contactName ?? 'Contato' }}</h2>
          <p v-if="link" class="agp-sub">{{ [link.organizationName, link.ownerName ? `responsável ${link.ownerName}` : 'sem responsável'].filter(Boolean).join(' · ') }}</p>
          <p v-else class="agp-sub">Ainda não está no Agendor</p>
        </div>
        <button type="button" class="agp-x" aria-label="Fechar" @click="emit('close')">×</button>
      </header>

      <div class="agp-body">
        <p v-if="state.loading.value && !panel" class="agp-muted">Carregando o Agendor…</p>
        <p v-if="state.problem.value" class="agp-problem">{{ state.problem.value }}</p>

        <!-- Divergência: quem atende não é o dono do lead -->
        <div v-if="divergence" class="agp-warn">
          <span>Você está atendendo, mas o lead é de <b>{{ link?.ownerName }}</b>.</span>
          <button v-if="conversationId && panel?.ownerAgentId" type="button" class="agp-btn agp-btn--sm" :disabled="busy" @click="transferTo(panel!.ownerAgentId!, link?.ownerName ?? '')">
            Transferir a conversa pra {{ firstName(link?.ownerName) }}
          </button>
        </div>

        <!-- ── Sem vínculo: enviar ou procurar ── -->
        <section v-if="panel && !link" ref="secSend" class="agp-sec">
          <h3>Enviar para o Agendor</h3>
          <p class="agp-muted">Procura antes pelo telefone: se a pessoa já estiver lá, só vincula.</p>
          <div class="agp-grid">
            <label class="agp-field agp-wide"><span>Nome</span><input id="agp-name" v-model="send.nome" class="agp-input" /></label>
            <label class="agp-field agp-wide"><span>E-mail</span><input id="agp-email" v-model="send.email" class="agp-input" type="email" placeholder="opcional" /></label>
            <label class="agp-field"><span>Empresa</span><input id="agp-org" v-model="send.empresa" class="agp-input" placeholder="opcional" /></label>
            <label class="agp-field"><span>CNPJ</span><input id="agp-cnpj" v-model="send.cnpj" class="agp-input" inputmode="numeric" placeholder="opcional" /></label>
            <label class="agp-field agp-wide"><span>Responsável</span>
              <select id="agp-send-owner" v-model="send.responsavelId" class="agp-input">
                <option value="">{{ panel.myUserId ? 'Eu' : 'Sem responsável' }}</option>
                <option v-for="u in panel.users" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </label>
          </div>
          <div class="agp-row">
            <button type="button" class="agp-btn agp-btn--main" :disabled="busy || !send.nome.trim()" @click="doSend">Enviar para o Agendor</button>
            <button type="button" class="agp-btn" :disabled="busy" @click="doFind">Procurar pelo telefone</button>
          </div>
          <ul v-if="candidates.length" class="agp-cands">
            <li v-for="c in candidates" :key="c.id">
              <button type="button" class="agp-cand" :disabled="busy" @click="doChoose(c.id)">
                <b>{{ c.name }}</b><small>{{ [c.organizationName, c.ownerName ? `resp. ${c.ownerName}` : null].filter(Boolean).join(' · ') || `id ${c.id}` }}</small>
              </button>
            </li>
          </ul>
        </section>

        <template v-if="panel && link">
          <!-- ── Negócios ── -->
          <section ref="secDeal" class="agp-sec">
            <div class="agp-sec-head">
              <h3>Negócios</h3>
              <button type="button" class="agp-link" @click="openForm = openForm === 'deal' ? null : 'deal'">{{ openForm === 'deal' ? 'Cancelar' : '+ Criar negócio' }}</button>
            </div>

            <form v-if="openForm === 'deal'" class="agp-form" @submit.prevent="doCreateDeal">
              <label class="agp-field agp-wide"><span>Título</span><input id="agp-deal-title" v-model="deal.titulo" class="agp-input" required /></label>
              <label class="agp-field"><span>Funil</span>
                <select id="agp-deal-funnel" v-model="deal.funilId" class="agp-input" @change="deal.etapa = stagesOf(deal.funilId)[0]?.sequence ?? 1">
                  <option v-for="f in panel.funnels" :key="f.id" :value="f.id">{{ f.name }}</option>
                </select>
              </label>
              <label class="agp-field"><span>Etapa</span>
                <select id="agp-deal-stage" v-model.number="deal.etapa" class="agp-input">
                  <option v-for="s in stagesOf(deal.funilId)" :key="s.id" :value="s.sequence">{{ s.sequence }}. {{ s.name }}</option>
                </select>
              </label>
              <label class="agp-field"><span>Valor (R$)</span><input id="agp-deal-value" v-model="deal.valor" class="agp-input" inputmode="decimal" placeholder="opcional" /></label>
              <div class="agp-row agp-wide"><button type="submit" class="agp-btn agp-btn--main" :disabled="busy || !deal.titulo.trim() || !deal.funilId">Criar negócio</button></div>
            </form>

            <p v-if="!panel.deals.length && openForm !== 'deal'" class="agp-muted">Nenhum negócio ainda.</p>
            <article v-for="d in panel.deals" :key="d.id" class="agp-deal" :class="`is-${d.status}`">
              <div class="agp-deal-top">
                <b>{{ d.title }}</b>
                <span class="agp-pill" :class="`pill-${d.status}`">{{ STATUS_LABEL[d.status] }}</span>
              </div>
              <div class="agp-deal-meta">
                {{ [d.funnelName, d.stageName].filter(Boolean).join(' · ') || 'sem etapa' }}<template v-if="d.value"> · {{ money(d.value) }}</template>
                <template v-if="d.ownerName"> · {{ d.ownerName }}</template>
              </div>
              <!-- Linha de etapas: clique leva o negócio pra etapa -->
              <div v-if="d.status === 'ongoing' && d.funnelId" class="agp-stages" :aria-label="`Etapas de ${d.title}`">
                <button v-for="s in stagesOf(d.funnelId)" :key="s.id" type="button" class="agp-stage"
                        :class="{ done: (d.stageSequence ?? 0) > s.sequence, here: d.stageSequence === s.sequence }"
                        :disabled="busy || !canMove || d.stageSequence === s.sequence"
                        :title="canMove ? `Mover para ${s.name}` : 'Sem permissão pra mover etapa (agendor.mover_etapa)'"
                        @click="doMove(d, s)">{{ s.name }}</button>
              </div>
              <div class="agp-row">
                <template v-if="d.status === 'ongoing' && canWinLose">
                  <button type="button" class="agp-btn agp-btn--sm agp-btn--won" :disabled="busy" @click="doStatus(d, 'won')">Marcar ganho</button>
                  <button type="button" class="agp-btn agp-btn--sm" :disabled="busy" @click="losing = losing === d.id ? null : d.id">Marcar perdido</button>
                </template>
                <button v-else-if="d.status !== 'ongoing' && canWinLose" type="button" class="agp-btn agp-btn--sm" :disabled="busy" @click="doStatus(d, 'ongoing')">Reabrir</button>
                <a v-if="d.webUrl" class="agp-link" :href="d.webUrl" target="_blank" rel="noopener">Abrir ↗</a>
              </div>
              <div v-if="losing === d.id" class="agp-row">
                <select :id="`agp-loss-${d.id}`" v-model="lossReason" class="agp-input agp-grow" aria-label="Motivo da perda">
                  <option value="">Motivo da perda…</option>
                  <option v-for="r in panel.lossReasons" :key="r.id" :value="r.id">{{ r.name }}</option>
                </select>
                <button type="button" class="agp-btn agp-btn--sm agp-btn--danger" :disabled="busy || !lossReason" @click="doStatus(d, 'lost')">Confirmar</button>
              </div>
            </article>
          </section>

          <!-- ── Retornos e anotações ── -->
          <section ref="secTask" class="agp-sec">
            <div class="agp-sec-head">
              <h3>Retornos</h3>
              <span class="agp-row agp-row--tight">
                <button type="button" class="agp-link" @click="openForm = openForm === 'task' ? null : 'task'">{{ openForm === 'task' ? 'Cancelar' : '+ Criar retorno' }}</button>
                <button type="button" class="agp-link" @click="openForm = openForm === 'note' ? null : 'note'">{{ openForm === 'note' ? 'Cancelar' : '+ Anotação' }}</button>
              </span>
            </div>

            <form v-if="openForm === 'task'" class="agp-form" @submit.prevent="doTask(true)">
              <label class="agp-field"><span>Quando</span><input id="agp-task-due" v-model="task.vencimento" class="agp-input" type="datetime-local" required /></label>
              <label class="agp-field"><span>Tipo</span>
                <select id="agp-task-type" v-model="task.tipo" class="agp-input">
                  <option value="">Tarefa</option>
                  <option v-for="(label, key) in AGENDOR_TASK_TYPES" :key="key" :value="key">{{ label }}</option>
                </select>
              </label>
              <label class="agp-field agp-wide"><span>Para</span>
                <select id="agp-task-owner" v-model="task.responsavelId" class="agp-input">
                  <option value="">{{ link.ownerName ? `Responsável do lead (${link.ownerName})` : 'Eu' }}</option>
                  <option v-for="u in panel.users" :key="u.id" :value="u.id">{{ u.name }}</option>
                </select>
              </label>
              <label class="agp-field agp-wide"><span>O que fazer</span><textarea id="agp-task-text" v-model="task.texto" class="agp-input" rows="2" required></textarea></label>
              <div class="agp-row agp-wide"><button type="submit" class="agp-btn agp-btn--main" :disabled="busy || !task.texto.trim() || !task.vencimento">Criar retorno</button></div>
            </form>

            <form v-if="openForm === 'note'" class="agp-form" @submit.prevent="doTask(false)">
              <label class="agp-field agp-wide"><span>Anotação</span><textarea id="agp-note-text" v-model="note" class="agp-input" rows="3" required placeholder="Ex: pediu proposta pra 20 vagas de estágio"></textarea></label>
              <div class="agp-row agp-wide"><button type="submit" class="agp-btn agp-btn--main" :disabled="busy || !note.trim()">Registrar anotação</button></div>
            </form>

            <p v-if="!panel.openTasks.length && !openForm?.match(/task|note/)" class="agp-muted">Nenhum retorno em aberto.</p>
            <ul class="agp-tasks">
              <li v-for="t in panel.openTasks" :key="t.id" :class="{ late: t.dueAt && new Date(t.dueAt) < new Date() }">
                <span class="agp-task-when">{{ t.dueAt ? when(t.dueAt) : 'sem data' }}</span>
                <span class="agp-task-text">{{ t.text }}<small v-if="t.assignedNames.length"> · {{ t.assignedNames.join(', ') }}</small></span>
              </li>
            </ul>
          </section>

          <!-- ── Responsável ── -->
          <section ref="secOwner" class="agp-sec">
            <h3>Responsável</h3>
            <p class="agp-muted">{{ link.ownerName ? `Hoje: ${link.ownerName}` : 'Sem responsável no Agendor.' }}</p>
            <div class="agp-row">
              <button v-if="panel.myUserId && link.ownerName !== myName" type="button" class="agp-btn agp-btn--main" :disabled="busy" @click="doOwner(null)">Assumir lead</button>
              <span v-else-if="!panel.myUserId" class="agp-muted">Seu usuário no Agendor não está definido (Admin › Agendor).</span>
            </div>
            <div class="agp-row">
              <select id="agp-pass-to" v-model="passTo" class="agp-input agp-grow" aria-label="Passar lead para">
                <option value="">Passar lead para…</option>
                <option v-for="u in otherUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
              <button type="button" class="agp-btn" :disabled="busy || !passTo" @click="doOwner(passTo)">Passar</button>
            </div>
          </section>
        </template>
      </div>

      <footer v-if="link" class="agp-foot">
        <a v-if="link.webUrl" class="agp-btn" :href="link.webUrl" target="_blank" rel="noopener">Ver no Agendor ↗</a>
        <button type="button" class="agp-btn" :disabled="busy || state.loading.value" @click="doRefresh">{{ state.loading.value ? 'Atualizando…' : 'Atualizar' }}</button>
        <button type="button" class="agp-btn agp-btn--danger" :disabled="busy" @click="doUnlink">Desvincular</button>
      </footer>
    </aside>
  </Teleport>
</template>

<script setup lang="ts">
// Painel do Agendor na conversa (plano-agendor.md, fase 4): enviar/procurar, negócios (criar, linha de etapas, ganho
// e perdido), retornos e anotações, responsável (assumir/passar) e o aviso quando quem atende não é o dono do lead.
// Mover etapa e ganho/perda respeitam as permissões agendor.mover_etapa e agendor.ganho_perda.
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { usePermission } from '@/composables/usePermission'
import type { AgendorPanelFocus, AgendorPanelState } from '@/composables/useAgendorPanel'
import { setAgendorLink } from '@/composables/useAgendor'
import { assignConversation } from '@/services/wootdashConversations'
import {
  AGENDOR_TASK_TYPES, createAgendorDeal, createAgendorPerson, createAgendorTask, findAgendorContact, linkAgendorPerson, moveAgendorDeal,
  setAgendorDealStatus, setAgendorOwner, unlinkAgendorContact, type AgendorDeal, type AgendorDealStatus, type AgendorPerson, type AgendorStage,
} from '@/services/wootdashAgendor'

const props = defineProps<{
  state: AgendorPanelState
  contactId: string
  conversationId: string | null
  conversationAgentId: string | null
  contactName: string | null
  focus?: AgendorPanelFocus
}>()
const emit = defineEmits<{ close: [] }>()

const STATUS_LABEL: Record<AgendorDealStatus, string> = { ongoing: 'Em andamento', won: 'Ganho', lost: 'Perdido' }

const store = useStore()
const { can } = usePermission()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const myAgentId = computed<string>(() => store.getters.authUser?.wootdash_agent_id ?? '')
const canMove = computed(() => can('agendor.mover_etapa'))
const canWinLose = computed(() => can('agendor.ganho_perda'))

const panel = computed(() => props.state.panel.value)
const link = computed(() => panel.value?.link ?? null)
const busy = computed(() => props.state.busy.value)
const myName = computed(() => panel.value?.users.find(u => u.id === panel.value?.myUserId)?.name ?? null)
const otherUsers = computed(() => (panel.value?.users ?? []).filter(u => u.name !== link.value?.ownerName))
const divergence = computed(() => !!link.value?.ownerName && !!panel.value?.ownerAgentId && !!props.conversationAgentId
  && props.conversationAgentId.toLowerCase() === myAgentId.value.toLowerCase() && panel.value.ownerAgentId.toLowerCase() !== myAgentId.value.toLowerCase())

const openForm = ref<'deal' | 'task' | 'note' | null>(null)
const losing = ref<string | null>(null)
const lossReason = ref('')
const passTo = ref('')
const note = ref('')
const candidates = ref<AgendorPerson[]>([])
const send = reactive({ nome: props.contactName ?? '', email: '', empresa: '', cnpj: '', responsavelId: '' })
const deal = reactive({ titulo: '', funilId: '', etapa: 1, valor: '' })
const task = reactive({ vencimento: '', tipo: 'LIGACAO', responsavelId: '', texto: '' })

const root = ref<HTMLElement | null>(null)
const secSend = ref<HTMLElement | null>(null)
const secDeal = ref<HTMLElement | null>(null)
const secTask = ref<HTMLElement | null>(null)
const secOwner = ref<HTMLElement | null>(null)

function stagesOf(funnelId: string | null): AgendorStage[] {
  return panel.value?.funnels.find(f => f.id === funnelId)?.stages ?? []
}

function resetDeal() {
  deal.titulo = `${props.contactName ?? 'Contato'} — WhatsApp`
  deal.funilId = panel.value?.defaultFunnelId ?? panel.value?.funnels[0]?.id ?? ''
  deal.etapa = panel.value?.defaultStageSequence ?? stagesOf(deal.funilId)[0]?.sequence ?? 1
  deal.valor = ''
}

/** Amanhã às 9h, no fuso do navegador (input datetime-local). */
function tomorrowAtNine() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`
}

function applyFocus(focus: AgendorPanelFocus | undefined) {
  if (focus === 'deal' || focus === 'task' || focus === 'note') openForm.value = focus
  if (focus === 'deal') resetDeal()
  if (focus === 'task' && !task.vencimento) task.vencimento = tomorrowAtNine()
  void nextTick(() => {
    const target = { send: secSend, deal: secDeal, task: secTask, note: secTask, owner: secOwner }[focus ?? 'send']?.value
    target?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    target?.querySelector<HTMLElement>('input, textarea, select, button')?.focus()
  })
}

onMounted(() => { applyFocus(props.focus); if (!props.focus) root.value?.querySelector<HTMLElement>('button')?.focus() })
watch(() => props.focus, f => applyFocus(f))
watch(panel, p => { if (p && !deal.funilId) resetDeal() }, { immediate: true })
watch(openForm, f => {
  if (f === 'deal') resetDeal()
  if (f === 'task' && !task.vencimento) task.vencimento = tomorrowAtNine()
})

const conv = () => props.conversationId

async function doSend() {
  const ok = await props.state.run(() => createAgendorPerson(token.value, props.contactId, conv(), {
    nome: send.nome.trim(), email: send.email.trim() || null, empresa: send.empresa.trim() || null, cnpj: send.cnpj.trim() || null,
    responsavelId: send.responsavelId || null,
  }))
  if (ok) openForm.value = null
}

async function doFind() {
  const result = await findAgendorContact(token.value, props.contactId, conv())
  const match = result.match
  candidates.value = match?.outcome === 'Ambiguous' ? match.candidates ?? [] : []
  if (match?.outcome === 'Linked' || match?.outcome === 'AlreadyLinked') {
    setAgendorLink(props.contactId, match.link)
    await props.state.load()
  } else {
    props.state.setProblem(result.problem ?? match?.problem ?? (match?.outcome === 'NotFound' ? 'Ninguém no Agendor com o telefone deste contato. Use "Enviar para o Agendor".' : null))
  }
}

async function doChoose(personId: string) {
  const result = await linkAgendorPerson(token.value, props.contactId, personId, conv())
  if (result.match?.link) {
    setAgendorLink(props.contactId, result.match.link)
    candidates.value = []
    await props.state.load()
  } else {
    props.state.setProblem(result.problem)
  }
}

async function doCreateDeal() {
  const value = deal.valor.trim() ? Number(deal.valor.replace(/\./g, '').replace(',', '.')) : null
  if (value !== null && (!Number.isFinite(value) || value < 0)) { props.state.setProblem('Valor inválido.'); return }
  const ok = await props.state.run(() => createAgendorDeal(token.value, props.contactId, conv(), { titulo: deal.titulo.trim(), funilId: deal.funilId, etapa: deal.etapa, valor: value }))
  if (ok) openForm.value = null
}

function doMove(d: AgendorDeal, s: AgendorStage) {
  void props.state.run(() => moveAgendorDeal(token.value, props.contactId, conv(), d.id, d.funnelId!, s.sequence))
}

async function doStatus(d: AgendorDeal, status: AgendorDealStatus) {
  if (status === 'won') {
    const ok = await Swal.fire({ title: 'Marcar como ganho?', text: `"${d.title}" vira ganho no Agendor e a conversa ganha a etiqueta de cliente.`, icon: 'question',
      showCancelButton: true, confirmButtonText: 'Marcar ganho', cancelButtonText: 'Cancelar' })
    if (!ok.isConfirmed) return
  }
  const done = await props.state.run(() => setAgendorDealStatus(token.value, props.contactId, conv(), d.id, status, status === 'lost' ? lossReason.value : null))
  if (done) { losing.value = null; lossReason.value = '' }
}

async function doTask(withDue: boolean) {
  const ok = await props.state.run(() => createAgendorTask(token.value, props.contactId, conv(), withDue
    ? { texto: task.texto.trim(), vencimento: new Date(task.vencimento).toISOString(), tipo: task.tipo || null, responsavelId: task.responsavelId || null }
    : { texto: note.value.trim() }))
  if (!ok) return
  openForm.value = null
  if (withDue) { task.texto = ''; task.vencimento = '' } else note.value = ''
}

async function doOwner(userId: string | null) {
  const target = userId ? panel.value?.users.find(u => u.id === userId) : null
  const ok = await props.state.run(() => setAgendorOwner(token.value, props.contactId, conv(), userId))
  if (!ok) return
  passTo.value = ''
  // Passou pra alguém que também é agente aqui: oferece levar a conversa junto.
  if (target?.agentId && props.conversationId && target.agentId.toLowerCase() !== (props.conversationAgentId ?? '').toLowerCase()) {
    const ask = await Swal.fire({ title: `Transferir a conversa para ${target.name} também?`, text: 'O lead já é dele no Agendor.', icon: 'question',
      showCancelButton: true, confirmButtonText: 'Transferir', cancelButtonText: 'Só o lead' })
    if (ask.isConfirmed) await transferTo(target.agentId, target.name)
  }
}

async function transferTo(agentId: string, name: string) {
  if (!props.conversationId) return
  const ok = await assignConversation(token.value, props.conversationId, agentId)
  props.state.setProblem(ok ? null : `Não deu pra transferir a conversa para ${name}.`)
}

async function doRefresh() {
  await findAgendorContact(token.value, props.contactId, conv())
  await props.state.load(true)
}

async function doUnlink() {
  if (!link.value) return
  const ok = await Swal.fire({ title: 'Desvincular do Agendor?', text: `O contato deixa de estar ligado a ${link.value.personName}. Nada é apagado no Agendor.`,
    icon: 'warning', showCancelButton: true, confirmButtonText: 'Desvincular', cancelButtonText: 'Cancelar' })
  if (!ok.isConfirmed) return
  const result = await unlinkAgendorContact(token.value, props.contactId, conv())
  if (result.problem) { props.state.setProblem(result.problem); return }
  setAgendorLink(props.contactId, null)
  await props.state.load()
}

function firstName(name: string | null | undefined) { return (name ?? '').split(' ')[0] || 'ele' }
function money(value: number) { return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }
function when(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.agp-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .25); z-index: 3990; }
.agp {
  position: fixed; top: 0; right: 0; bottom: 0; width: min(420px, 100vw); z-index: 4000; display: flex; flex-direction: column;
  background: var(--white, #fff); color: var(--ink-80, #1f2937); box-shadow: -12px 0 32px rgba(15, 23, 42, .18); font-size: 13px;
}
.agp-head { display: flex; justify-content: space-between; gap: 12px; padding: 16px 18px 12px; border-bottom: 1px solid var(--ink-10, #e5e7eb); }
.agp-brand { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #0f6f66; }
.agp-title { margin: 2px 0 0; font-size: 17px; font-weight: 700; }
.agp-sub { margin: 2px 0 0; color: var(--ink-50, #6b7280); font-size: 12.5px; }
.agp-x { border: none; background: none; font-size: 24px; line-height: 1; color: var(--ink-40, #9ca3af); cursor: pointer; align-self: flex-start; }
.agp-body { flex: 1; overflow-y: auto; padding: 12px 18px 18px; display: flex; flex-direction: column; gap: 14px; }
.agp-foot { display: flex; gap: 8px; flex-wrap: wrap; padding: 12px 18px; border-top: 1px solid var(--ink-10, #e5e7eb); }
.agp-sec { display: flex; flex-direction: column; gap: 8px; scroll-margin-top: 8px; }
.agp-sec h3 { margin: 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--ink-60, #4b5563); }
.agp-sec-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.agp-muted { margin: 0; color: var(--ink-40, #6b7280); font-size: 12.5px; }
.agp-problem { margin: 0; color: #b45309; font-size: 12.5px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px; }
.agp-warn { display: flex; flex-direction: column; gap: 6px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 8px; padding: 8px 10px; font-size: 12.5px; }
.agp-grid, .agp-form { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.agp-form { background: var(--ink-02, #f9fafb); border: 1px solid var(--ink-10, #e5e7eb); border-radius: 8px; padding: 10px; }
.agp-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.agp-field span { font-size: 11.5px; font-weight: 600; color: var(--ink-60, #4b5563); }
.agp-wide { grid-column: 1 / -1; }
.agp-input { width: 100%; box-sizing: border-box; border: 1px solid var(--ink-10, #e5e7eb); border-radius: 6px; padding: 6px 8px; font: inherit; font-size: 12.5px; background: var(--white, #fff); color: inherit; }
.agp-input:focus { outline: none; border-color: #0f6f66; }
textarea.agp-input { resize: vertical; }
.agp-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.agp-row--tight { gap: 10px; }
.agp-grow { flex: 1; min-width: 0; }
.agp-btn {
  border: 1px solid var(--ink-10, #e5e7eb); background: transparent; color: var(--ink-70, #374151); border-radius: 6px; padding: 6px 12px;
  font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center;
}
.agp-btn:hover:not(:disabled) { background: var(--ink-05, #f3f4f6); }
.agp-btn:disabled { opacity: .5; cursor: not-allowed; }
.agp-btn--sm { padding: 4px 9px; font-size: 12px; }
.agp-btn--main { background: #0f6f66; border-color: #0f6f66; color: #fff; }
.agp-btn--main:hover:not(:disabled) { background: #0b5a53; }
.agp-btn--won { color: #15803d; border-color: #bbf7d0; }
.agp-btn--danger { color: #b91c1c; }
.agp-link { border: none; background: none; color: #0f6f66; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0; text-decoration: none; }
.agp-link:hover { text-decoration: underline; }
.agp-deal { border: 1px solid var(--ink-10, #e5e7eb); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 6px; }
.agp-deal.is-won { border-color: #bbf7d0; background: #f0fdf4; }
.agp-deal.is-lost { opacity: .75; }
.agp-deal-top { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; }
.agp-deal-meta { color: var(--ink-50, #6b7280); font-size: 12px; }
.agp-pill { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }
.pill-ongoing { background: #e0f0ed; color: #0f6f66; }
.pill-won { background: #dcfce7; color: #15803d; }
.pill-lost { background: #fee2e2; color: #b91c1c; }
.agp-stages { display: flex; flex-wrap: wrap; gap: 4px; }
.agp-stage { border: 1px solid var(--ink-10, #e5e7eb); background: var(--white, #fff); border-radius: 999px; padding: 3px 9px; font: inherit; font-size: 11.5px; cursor: pointer; color: var(--ink-60, #4b5563); }
.agp-stage.done { background: #e0f0ed; border-color: #b6ddd7; color: #0f6f66; }
.agp-stage.here { background: #0f6f66; border-color: #0f6f66; color: #fff; font-weight: 700; cursor: default; }
.agp-stage:disabled:not(.here) { cursor: not-allowed; opacity: .6; }
.agp-tasks { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.agp-tasks li { display: grid; grid-template-columns: 92px 1fr; gap: 8px; padding: 6px 8px; border-radius: 6px; background: var(--ink-02, #f9fafb); }
.agp-tasks li.late .agp-task-when { color: #b91c1c; font-weight: 700; }
.agp-task-when { font-variant-numeric: tabular-nums; color: var(--ink-60, #4b5563); font-size: 12px; }
.agp-task-text { min-width: 0; overflow-wrap: anywhere; }
.agp-task-text small { color: var(--ink-40, #6b7280); }
.agp-cands { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.agp-cand { width: 100%; text-align: left; border: 1px solid var(--ink-10, #e5e7eb); background: transparent; border-radius: 6px; padding: 6px 8px; display: flex; flex-direction: column; cursor: pointer; font: inherit; color: inherit; }
.agp-cand:hover:not(:disabled) { border-color: #0f6f66; }
.agp-cand small { color: var(--ink-40, #6b7280); font-size: 11.5px; }
</style>
